import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type LoadingAction = 'loading' | 'saving' | 'deleting' | 'idle';

export interface LoadingState {
  active: boolean;
  pending: number;
  action: LoadingAction;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private pending = 0;
  private writePending = 0;
  private deletePending = 0;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly delayMs = 180;

  private readonly stateSubject = new BehaviorSubject<LoadingState>({
    active: false,
    pending: 0,
    action: 'idle',
    message: '',
  });

  readonly state$: Observable<LoadingState> = this.stateSubject.asObservable();

  get snapshot(): LoadingState {
    return this.stateSubject.value;
  }

  start(method: string): void {
    this.pending++;
    const m = (method || 'GET').toUpperCase();
    if (m === 'DELETE') {
      this.deletePending++;
    } else if (m === 'POST' || m === 'PUT' || m === 'PATCH') {
      this.writePending++;
    }

    if (this.pending === 1) {
      this.showTimer = setTimeout(() => this.emit(true), this.delayMs);
    } else {
      this.emit(this.stateSubject.value.active);
    }
  }

  stop(method: string): void {
    this.pending = Math.max(0, this.pending - 1);
    const m = (method || 'GET').toUpperCase();
    if (m === 'DELETE') {
      this.deletePending = Math.max(0, this.deletePending - 1);
    } else if (m === 'POST' || m === 'PUT' || m === 'PATCH') {
      this.writePending = Math.max(0, this.writePending - 1);
    }

    if (this.pending === 0) {
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
      this.emit(false);
    } else {
      this.emit(this.stateSubject.value.active);
    }
  }

  private emit(active: boolean): void {
    const action = this.resolveAction();
    this.stateSubject.next({
      active,
      pending: this.pending,
      action,
      message: this.messageFor(action),
    });
  }

  private resolveAction(): LoadingAction {
    if (this.deletePending > 0) {
      return 'deleting';
    }
    if (this.writePending > 0) {
      return 'saving';
    }
    if (this.pending > 0) {
      return 'loading';
    }
    return 'idle';
  }

  private messageFor(action: LoadingAction): string {
    switch (action) {
      case 'saving':
        return 'Saving…';
      case 'deleting':
        return 'Deleting…';
      case 'loading':
        return 'Loading…';
      default:
        return '';
    }
  }
}
